// ── Deterioration List — assets getting worse over time ──

import { motion } from "framer-motion";
import type { DeterioratingAsset } from "../types/graph";
import { severityColor } from "../utils/colors";

interface DeteriorationListProps {
  assets: DeterioratingAsset[];
  maxItems?: number;
}

export default function DeteriorationList({
  assets,
  maxItems = 8,
}: DeteriorationListProps) {
  if (assets.length === 0) {
    return (
      <div className="flex items-center justify-center h-16 text-fl-evergreen-60 text-sm">
        No deterioration detected
      </div>
    );
  }

  const displayed = assets.slice(0, maxItems);

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs uppercase tracking-widest text-fl-forest-60">
          Deteriorating Assets
        </span>
        <span className="text-xs text-fl-evergreen-60">
          ({assets.length} found)
        </span>
      </div>

      <div className="space-y-2">
        {displayed.map((a, i) => (
          <motion.div
            key={a.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.05, duration: 0.3 }}
            className="flex items-center gap-3 p-2 rounded-lg bg-fl-sand-20
                       border border-fl-evergreen-20 hover:border-fl-forest-40 transition-colors"
          >
            {/* Delta badge */}
            <div
              className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center
                         font-bold text-sm"
              style={{
                backgroundColor: severityColor(a.currentSeverity) + "20",
                color: severityColor(a.currentSeverity),
              }}
            >
              +{a.delta}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="text-sm text-fl-evergreen truncate">
                {a.element ?? a.area ?? "Unknown"}
              </div>
              <div className="text-xs text-fl-evergreen-60 truncate">
                {a.area && a.element ? a.area : a.currentRating}
              </div>
            </div>

            {/* Severity progression */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <span
                className="w-6 h-6 rounded flex items-center justify-center text-xs font-medium"
                style={{
                  backgroundColor: severityColor(a.prevSeverity) + "30",
                  color: severityColor(a.prevSeverity),
                }}
              >
                {a.prevSeverity}
              </span>
              <svg
                className="w-4 h-4 text-fl-evergreen-60"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
              <span
                className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold"
                style={{
                  backgroundColor: severityColor(a.currentSeverity) + "30",
                  color: severityColor(a.currentSeverity),
                }}
              >
                {a.currentSeverity}
              </span>
            </div>
          </motion.div>
        ))}
      </div>

      {assets.length > maxItems && (
        <div className="text-xs text-fl-evergreen-60 mt-2 text-center">
          +{assets.length - maxItems} more
        </div>
      )}
    </div>
  );
}
